import React, { useCallback, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { BellOutline } from '@lyearn/icons';
import { Box, IconButton, Popover, Tooltip } from '@lyearn/ui';
import BadgePreviewModal from '@/modules/gamification/components/BadgeGroupView/BadgePreviewModal';
import PointsPreviewModal from '@/modules/gamification/components/Points/PointsPreviewModal';
import { MediaKindEnum, NotificationActionTypeEnum, PointsConfigTypeEnum } from '@/types/schema';

import useNotificationCount from '../NotificationButton/hooks/useNotificationCount';
import NotificationTray from '../NotificationTray';
import { NotificationDetailsFragment } from '../NotificationTray/graphql/fragments/generated/NotificationDetails';

type BadgeNotificationType = {
  id?: string;
  message?: string;
  icon?: string | null;
};

const NotificationBell = () => {
  const { t } = useTranslation();
  const { onClick, unresolvedNotificationCount, unreadNotificationsCount, toggle, open } =
    useNotificationCount();

  const [currentBadge, setCurrentBadge] = useState<BadgeNotificationType | undefined>(undefined);
  const [pointsMessage, setPointsMessage] = useState<string | undefined>(undefined);

  const onClickBadgeType = useCallback((notif: NotificationDetailsFragment) => {
    if (notif.type === NotificationActionTypeEnum.BadgeUnlocked) {
      setCurrentBadge({
        id: notif.id,
        message: notif.message,
        icon: notif.icon ?? undefined,
      });
    }
  }, []);

  const onClickPointsAwarded = useCallback((notif: NotificationDetailsFragment) => {
    if (notif.type === NotificationActionTypeEnum.PointsAwarded) {
      setPointsMessage(notif.message);
    }
  }, []);

  const closeBadgeModal = useCallback(() => setCurrentBadge(undefined), []);
  const closePointsModal = useCallback(() => setPointsMessage(undefined), []);

  return (
    <>
      <Popover open={open} onOpenChange={onClick}>
        <Popover.Button>
          <Tooltip placement="bottom" title={`${t('Common:Notifications.Header')}`}>
            <IconButton
              appearance="secondary"
              aria-label={`${t('Common:Notifications.Header')}`}
              className="rounded-8"
              data-action="Open Notification Tray"
              shape="square"
              size="m"
              variant="outlined">
              <Box className="relative">
                <BellOutline className="h-20 w-20" />
                {unresolvedNotificationCount ? (
                  <Box className="absolute top-0 h-6 w-6 rounded-full bg-icon-error end-0" />
                ) : null}
              </Box>
            </IconButton>
          </Tooltip>
        </Popover.Button>
        <Popover.Content className="shadow-03" position="bottom-end" renderAs="popover">
          <NotificationTray
            toggleTray={toggle}
            unreadNotificationsCount={unreadNotificationsCount}
            onClickBadgeType={onClickBadgeType}
            onClickPointsAwarded={onClickPointsAwarded}
          />
        </Popover.Content>
      </Popover>
      {currentBadge ? (
        <BadgePreviewModal
          open
          badge={{
            id: currentBadge.id ?? '',
            name: 'Congratulations!',
            achievementRemark: currentBadge.message,
            pointsConfig: {
              points: 0,
              bonus: 0,
              type: PointsConfigTypeEnum.Custom,
            },
            media: currentBadge.icon
              ? {
                  url: currentBadge.icon,
                  kind: MediaKindEnum.Image,
                }
              : undefined,
          }}
          isPreview={false}
          showNext={false}
          showPrev={false}
          onClose={closeBadgeModal}
        />
      ) : null}
      {pointsMessage ? (
        <PointsPreviewModal open message={pointsMessage} onClose={closePointsModal} />
      ) : null}
    </>
  );
};

export default React.memo(NotificationBell);
